import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Search, ChevronRight, Newspaper, Clapperboard, Church, Trophy, Music, Baby,
  GraduationCap, HeartPulse, Briefcase, Film, Camera, Laugh, FlaskConical,
  Cpu, Gamepad2, Utensils, Plane, Heart, Shirt, Tv, ArrowUpDown, Sparkles,
  Crown, Monitor, Wifi, Shield, Eye, Play
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const ICON_MAP = {
  newspaper: Newspaper, clapperboard: Clapperboard, church: Church, trophy: Trophy,
  music: Music, baby: Baby, 'graduation-cap': GraduationCap, 'heart-pulse': HeartPulse,
  briefcase: Briefcase, film: Film, camera: Camera, laugh: Laugh, 'flask-conical': FlaskConical,
  cpu: Cpu, 'gamepad-2': Gamepad2, utensils: Utensils, plane: Plane, heart: Heart,
  shirt: Shirt, tv: Tv,
};

const FILTER_SLUGS = [
  { slug: 'all', icon: Tv },
  { slug: 'news', icon: Newspaper },
  { slug: 'entertainment', icon: Clapperboard },
  { slug: 'religion', icon: Church },
  { slug: 'sports', icon: Trophy },
  { slug: 'music', icon: Music },
  { slug: 'kids', icon: Baby },
  { slug: 'education', icon: GraduationCap },
  { slug: 'lifestyle', icon: HeartPulse },
  { slug: 'business', icon: Briefcase },
];

function CategoryCard({ cat, idx, lang }) {
  const { t } = useTranslation();
  const IconComp = ICON_MAP[cat.icon] || Tv;
  const name = lang === 'en' ? (cat.name_en || cat.name) : (cat.name_fr || cat.name);
  const desc = lang === 'en' ? (cat.description_en || cat.description) : (cat.description_fr || cat.description);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.05, duration: 0.5 }}
    >
      <Link
        to={`/categories/${cat.slug}`}
        data-testid={`category-card-${cat.slug}`}
        className="group block rounded-2xl overflow-hidden border border-white/[0.06] bg-[#0F172A] hover:border-[#FFC928]/30 hover:shadow-[0_8px_40px_rgba(255,201,40,0.12)] transition-all duration-400 relative"
      >
        {/* Image */}
        <div className="aspect-video relative overflow-hidden">
          {cat.image_url ? (
            <img
              src={cat.image_url}
              alt={name}
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${cat.accent_color}22, ${cat.accent_color}44)` }}>
              <IconComp size={48} style={{ color: cat.accent_color }} className="opacity-40" />
            </div>
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-transparent" />

          {/* Icon badge */}
          <div
            className="absolute top-3 left-3 w-10 h-10 rounded-xl flex items-center justify-center backdrop-blur-md border border-white/10"
            style={{ background: `${cat.accent_color}33` }}
          >
            <IconComp size={18} style={{ color: cat.accent_color }} />
          </div>

          {/* Featured badge */}
          {cat.is_featured === 1 && (
            <div className="absolute top-3 right-3 px-2 py-0.5 rounded-lg bg-[#FFC928]/90 text-[10px] font-bold text-black uppercase tracking-wider flex items-center gap-1">
              <Sparkles size={10} /> {lang === 'fr' ? 'Vedette' : 'Featured'}
            </div>
          )}

          {/* Hover play */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-14 h-14 bg-[#FFC928] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(255,201,40,0.5)] scale-75 group-hover:scale-100 transition-transform duration-300">
              <Eye size={22} className="text-black" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-white text-base mb-1 group-hover:text-[#FFC928] transition-colors line-clamp-1" style={{ fontFamily: 'Poppins' }}>
            {name}
          </h3>
          <p className="text-[#94A3B8] text-xs mb-3 line-clamp-2 leading-relaxed">{desc}</p>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium" style={{ color: cat.accent_color }}>
              {cat.channel_count} {lang === 'fr' ? 'chaînes' : 'channels'}
            </span>
            <span className="text-xs text-[#94A3B8] group-hover:text-[#FFC928] transition-colors flex items-center gap-1">
              {lang === 'fr' ? 'Voir' : 'View'} <ChevronRight size={12} />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Categories() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [sortBy, setSortBy] = useState("order");
  const filterRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/categories/public?lang=${lang}`)
      .then(res => { setCategories(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [lang]);

  // Filter labels
  const filterLabels = {
    all: lang === 'fr' ? 'Toutes' : 'All',
    news: lang === 'fr' ? 'Actualités' : 'News',
    entertainment: lang === 'fr' ? 'Divertissement' : 'Entertainment',
    religion: 'Religion',
    sports: lang === 'fr' ? 'Sport' : 'Sports',
    music: lang === 'fr' ? 'Musique' : 'Music',
    kids: lang === 'fr' ? 'Enfants' : 'Kids',
    education: lang === 'fr' ? 'Éducation' : 'Education',
    lifestyle: 'Lifestyle',
    business: 'Business',
  };

  const sortLabels = {
    order: lang === 'fr' ? 'Ordre par défaut' : 'Default Order',
    name_asc: lang === 'fr' ? 'Nom A–Z' : 'Name A–Z',
    name_desc: lang === 'fr' ? 'Nom Z–A' : 'Name Z–A',
    channels_desc: lang === 'fr' ? 'Plus de chaînes' : 'Most Channels',
    channels_asc: lang === 'fr' ? 'Moins de chaînes' : 'Fewest Channels',
  };

  // Apply filters
  let filtered = categories;
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(c =>
      (c.name_fr || '').toLowerCase().includes(q) ||
      (c.name_en || '').toLowerCase().includes(q) ||
      (c.description_fr || '').toLowerCase().includes(q) ||
      (c.description_en || '').toLowerCase().includes(q)
    );
  }
  if (activeFilter !== 'all') {
    filtered = filtered.filter(c => c.slug === activeFilter);
  }

  // Sort
  filtered = [...filtered].sort((a, b) => {
    if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
    if (sortBy === 'channels_desc') return (b.channel_count || 0) - (a.channel_count || 0);
    if (sortBy === 'channels_asc') return (a.channel_count || 0) - (b.channel_count || 0);
    return (a.display_order || 99) - (b.display_order || 99);
  });

  return (
    <div className="min-h-screen bg-[#050814]" data-testid="categories-page">
      {/* Hero Header */}
      <div className="relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0L60 30L30 60L0 30Z' fill='none' stroke='%23FFC928' stroke-width='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F1E] via-[#050814]/80 to-[#050814]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFC928]/[0.03] via-transparent to-[#FFC928]/[0.03]" />

        <div className="relative px-4 sm:px-6 lg:px-8 pt-8 pb-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#94A3B8] mb-6" data-testid="categories-breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">{lang === 'fr' ? 'Accueil' : 'Home'}</Link>
            <ChevronRight size={14} />
            <span className="text-[#FFC928] font-medium">{lang === 'fr' ? 'Catégories' : 'Categories'}</span>
          </nav>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3"
            style={{ fontFamily: 'Poppins' }}
            data-testid="categories-title"
          >
            {lang === 'fr' ? 'Catégories' : 'Categories'}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[#94A3B8] text-sm sm:text-base max-w-2xl"
          >
            {lang === 'fr'
              ? 'Explorez toutes les catégories de chaînes congolaises et internationales.'
              : 'Explore all Congolese and international channel categories.'}
          </motion.p>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6 max-w-lg"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={18} />
              <input
                type="text"
                data-testid="categories-search"
                placeholder={lang === 'fr' ? 'Rechercher une catégorie...' : 'Search categories...'}
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white placeholder-[#94A3B8]/60 focus:outline-none focus:ring-2 focus:ring-[#FFC928]/30 focus:border-[#FFC928]/30 transition-all text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="sticky top-0 z-30 bg-[#050814]/95 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center gap-3">
            <div
              ref={filterRef}
              className="flex-1 flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1"
              data-testid="category-filter-bar"
            >
              {FILTER_SLUGS.map(({ slug, icon: Icon }) => {
                const isActive = activeFilter === slug;
                return (
                  <button
                    key={slug}
                    onClick={() => setActiveFilter(slug)}
                    data-testid={`filter-${slug}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? 'bg-[#FFC928] text-black shadow-[0_0_15px_rgba(255,201,40,0.3)]'
                        : 'bg-white/[0.05] text-[#94A3B8] hover:bg-white/[0.08] hover:text-white border border-white/[0.06]'
                    }`}
                  >
                    <Icon size={14} />
                    {filterLabels[slug] || slug}
                  </button>
                );
              })}
            </div>

            {/* Sort Dropdown */}
            <div className="relative flex-shrink-0">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="categories-sort"
                className="appearance-none pl-8 pr-6 py-1.5 rounded-lg bg-white/[0.05] border border-white/[0.06] text-[#94A3B8] text-xs focus:outline-none focus:ring-1 focus:ring-[#FFC928]/30 cursor-pointer"
              >
                {Object.entries(sortLabels).map(([k, v]) => (
                  <option key={k} value={k} className="bg-[#0F172A] text-white">{v}</option>
                ))}
              </select>
              <ArrowUpDown size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8] pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="categories-skeleton">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="rounded-2xl bg-[#0F172A] border border-white/[0.06] overflow-hidden animate-pulse">
                <div className="aspect-video bg-white/[0.03]" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-white/[0.05] rounded w-2/3" />
                  <div className="h-3 bg-white/[0.03] rounded w-full" />
                  <div className="h-3 bg-white/[0.03] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="categories-empty">
            <Tv size={48} className="mx-auto text-[#94A3B8]/30 mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">
              {lang === 'fr' ? 'Aucune catégorie trouvée' : 'No categories found'}
            </h3>
            <p className="text-[#94A3B8] text-sm">
              {lang === 'fr' ? 'Essayez de modifier votre recherche.' : 'Try adjusting your search.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="categories-grid">
            {filtered.map((cat, idx) => (
              <CategoryCard key={cat.id} cat={cat} idx={idx} lang={lang} />
            ))}
          </div>
        )}

        {/* Premium Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 relative rounded-2xl overflow-hidden border border-[#FFC928]/20"
          data-testid="premium-banner"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#FFC928]/10 via-[#0F172A] to-[#FFC928]/10" />
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20Z' fill='none' stroke='%23FFC928' stroke-width='0.5'/%3E%3C/svg%3E")`,
            backgroundSize: '40px 40px'
          }} />
          <div className="relative px-6 py-10 sm:py-14 text-center">
            <Crown size={36} className="mx-auto text-[#FFC928] mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins' }}>
              {lang === 'fr' ? "Vivez l'expérience Djilu Premium" : 'Experience Djilu Premium'}
            </h2>
            <p className="text-[#94A3B8] text-sm sm:text-base mb-6 max-w-xl mx-auto flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
              <span className="flex items-center gap-1"><Monitor size={14} className="text-[#FFC928]" /> {lang === 'fr' ? 'Plus de 100 chaînes' : '100+ channels'}</span>
              <span>•</span>
              <span className="flex items-center gap-1"><Wifi size={14} className="text-[#FFC928]" /> {lang === 'fr' ? 'Qualité HD' : 'HD quality'}</span>
              <span>•</span>
              <span className="flex items-center gap-1"><Shield size={14} className="text-[#FFC928]" /> {lang === 'fr' ? 'Sans publicité' : 'Ad-free'}</span>
              <span>•</span>
              <span>{lang === 'fr' ? 'Regardez partout' : 'Watch anywhere'}</span>
            </p>
            <Link
              to="/membership"
              data-testid="premium-banner-cta"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-[#FFC928] text-black font-bold text-sm hover:bg-[#FFD54F] transition-all shadow-[0_0_30px_rgba(255,201,40,0.3)] hover:shadow-[0_0_40px_rgba(255,201,40,0.5)]"
            >
              <Crown size={16} />
              {lang === 'fr' ? 'Devenir Premium' : 'Become Premium'}
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
